import { useCallback, useEffect, useState } from 'react';
import { Printer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { UniversalWindow } from '@/components/ui/UniversalWindow';
import { getSDK } from '@/store/sdkStore';
import { useToastStore } from '@/store/toastStore';
import { friendlyError, logErrorToJournal } from '@/lib/errors';
import { downloadBytes, isPreviewableMime, printFormatFor, printFormatLabel, base64ToBlob } from '@/lib/printForm';

// ============================================================================
// PrintFormButton — ПЕЧАТЬ ОБЪЕКТА (шаг 6 учётного ядра; SSoT — сервер):
// кнопка «Печать» в шапке формы объекта. Детерминизм:
//   - нет печатных форм (is_print=true, parent_type='object') — команды НЕТ;
//   - 1 форма — рендер сразу; >1 — диалог выбора (синоним + формат);
//   - рендер ПО ЗАПИСИ (record_id): параметры {{Поле}} собирает СЕРВЕР
//     (значения + представления ссылок + русские имена + строки ТЧ);
//   - pdf/html — предпросмотр, остальное — скачивание; ошибки — явные.
// ============================================================================

interface PrintFormButtonProps {
  objectType: string; // catalog | document | task | order
  objectName: string; // код объекта (таблица/тип)
  recordId?: string | null;
  disabled?: boolean;
}

interface PrintLayout {
  id: string;
  name: string;
  synonym: string;
  layoutType: string;
}

interface RenderedPrint {
  layoutName: string;
  fileName: string;
  mimeType: string;
  previewUrl?: string;
  dataB64: string;
}

export function PrintFormButton({ objectType, objectName, recordId, disabled }: PrintFormButtonProps) {
  const [forms, setForms] = useState<PrintLayout[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [busyId, setBusyId] = useState('');
  const [preview, setPreview] = useState<RenderedPrint | null>(null);
  const showSuccess = useToastStore((s) => s.showSuccess);
  const showError = useToastStore((s) => s.showError);

  const loadForms = useCallback(async () => {
    if (!recordId) {
      setForms([]);
      return;
    }
    if (!['catalog', 'document', 'task', 'order'].includes(objectType)) {
      setForms([]);
      return;
    }
    try {
      const r: any = await getSDK().metadata.listLayouts({
        parentType: 'object', parentCode: objectName, isPrint: true,
      });
      setForms(((r?.items || []) as any[]).map((l) => ({
        id: String(l.id), name: String(l.name || ''), synonym: String(l.synonym || ''),
        layoutType: String(l.layoutType || ''),
      })));
    } catch (e) {
      logErrorToJournal(e, 'PrintFormButton.loadForms');
      setForms([]); // нет печатных форм / нет прав — команды «Печать» нет
    }
  }, [objectType, objectName, recordId]);

  useEffect(() => { void loadForms(); }, [loadForms]);

  /** Рендер печатной формы по записи (SSoT — сервер: params собирает он). */
  const renderForm = useCallback(async (form: PrintLayout) => {
    if (!recordId) {
      showError('Печать недоступна: сначала сохраните запись');
      return;
    }
    let format = '';
    try {
      format = printFormatFor(form.layoutType);
    } catch (e) {
      showError(friendlyError(e));
      return;
    }
    setBusyId(form.id);
    try {
      const r: any = await getSDK().metadata.renderLayout({
        layoutId: form.id, params: {}, format, recordId,
      });
      const dataB64 = String(r?.data || '');
      const mimeType = String(r?.mimeType || 'application/octet-stream');
      const fileName = String(r?.fileName || `${form.synonym || form.name}.${format}`);
      if (isPreviewableMime(mimeType)) {
        setPreview({ layoutName: form.synonym || form.name, fileName, mimeType, dataB64, previewUrl: URL.createObjectURL(base64ToBlob(dataB64, mimeType)) });
        setPickerOpen(false);
      } else {
        downloadBytes(fileName, mimeType, dataB64);
        showSuccess(`Печатная форма сформирована: ${fileName}`);
        setPickerOpen(false);
      }
    } catch (e) {
      logErrorToJournal(e, 'PrintFormButton.render');
      showError(friendlyError(e));
    } finally {
      setBusyId('');
    }
  }, [recordId, showError, showSuccess]);

  const onPrintClick = () => {
    if (!recordId) {
      showError('Печать недоступна: сначала сохраните запись');
      return;
    }
    if (forms.length === 0) {
      showError('У объекта нет печатных форм (макет → «Печатная форма»)');
      return;
    }
    if (forms.length === 1) {
      void renderForm(forms[0]); // 1 форма — рендер сразу
      return;
    }
    setPickerOpen(true); // >1 — выбор формы
  };

  // Нет печатных форм — команды «Печать» НЕТ (детерминизм, без заглушек).
  if (forms.length === 0) return null;

  return (
    <>
      <Button size="sm" variant="outline" onClick={onPrintClick}
        disabled={disabled || !!busyId} title="Печать (печатные формы объекта)">
        <Printer className="h-3.5 w-3.5 mr-1" />
        {busyId ? 'Формирование…' : 'Печать'}
      </Button>

      {pickerOpen && (
        <UniversalWindow open onClose={() => setPickerOpen(false)} title="Печать" blocking={false} width="max-w-md">
          <div className="p-4 space-y-2">
            <div className="text-xs text-muted-foreground">Выберите печатную форму:</div>
            {forms.map((f) => {
              let formatLabel = '—';
              try { formatLabel = printFormatLabel(printFormatFor(f.layoutType)); } catch { formatLabel = f.layoutType; }
              return (
                <button key={f.id} className="w-full flex items-center justify-between gap-2 rounded border px-3 py-2 text-xs hover:bg-accent/40"
                  disabled={!!busyId} onClick={() => void renderForm(f)}>
                  <span className="flex items-center gap-2">
                    <Printer className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="font-medium">{f.synonym || f.name}</span>
                  </span>
                  <span className="text-muted-foreground">{formatLabel}</span>
                </button>
              );
            })}
          </div>
        </UniversalWindow>
      )}

      {preview && (
        <UniversalWindow
          open
          onClose={() => { if (preview.previewUrl) URL.revokeObjectURL(preview.previewUrl); setPreview(null); }}
          title={`Печать: ${preview.layoutName}`}
          width="max-w-4xl"
        >
          <div className="p-3 flex flex-col gap-2">
            <iframe src={preview.previewUrl} title={preview.fileName} className="w-full h-[70vh] rounded border bg-white" />
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm"
                onClick={() => { if (preview.previewUrl) URL.revokeObjectURL(preview.previewUrl); setPreview(null); }}>
                Закрыть
              </Button>
              <Button size="sm" onClick={() => downloadBytes(preview.fileName, preview.mimeType, preview.dataB64)}>
                Скачать
              </Button>
            </div>
          </div>
        </UniversalWindow>
      )}
    </>
  );
}
